import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'

// components
import { Button, Notification, RadioButton } from '@bosch/react-frok'
import { LanguageSwitch } from '@/ui/components/LanguageSwitch/LanguageSwitch'
import DraggableLineSelection from '@/ui/components/LineOnboarding/DraggableLineSelection'

// utils
import { useSpace } from '@/hooks/useSpace'
import { useNavigation } from '@/hooks/useNavigation'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useConfig } from '@/hooks/useConfig'
import useWindowSize from '@/hooks/useWindowSize'
import useVirtualKeyboard from '@/hooks/useVirtualKeyboard'
import { SettingsProperties } from '@/ui/pages/Settings/types'
import useAvailableLines from '@/hooks/useAvailableLines'
import { useProductionLines } from '@/hooks/useProductionLines'
import StreamingDots from '@/ui/components/StreamingDots/StreamingDots'

// styles
import './Settings.scss'

const Settings = ({ information }: SettingsProperties) => {
    const { switchSpace, getActiveSpace, spaces } = useSpace()
    const { navigateTo } = useNavigation()
    const [roles, setRoles] = useState(['no roles found'])
    const [isUpdating, setIsUpdating] = useState(false)
    const { isMobile } = useWindowSize()
    useVirtualKeyboard()

    const { t } = useTranslation('general')
    const { isDarkTheme: darkTheme, toggleTheme, getConfig } = useConfig()
    const { rehydrateInformation, logout, getApiToken } = useAuthentication()

    const availableLines = useAvailableLines(spaces, getActiveSpace)
    const endpoint = getConfig()?.endpoint?.replace(/\/$/, '') ?? ''
    const preferencesApiEnabled = getConfig()?.features?.preferencesApi ?? false

    // The lines are the ones of the space the user is in
    const spaceId = getActiveSpace()?.id ?? ''

    const { rows, setRows, isSaving, isSaved, saveError, handleChange, getPreferredLinesFromApi } = useProductionLines({
        spaceId,
        endpoint,
        getApiToken,
        preferencesApiEnabled,
    })

    const name = information?.account?.name || <i>Jane Doe</i>
    const photo = information?.profilePhoto || 'images/placeholder.svg'

    useEffect(() => {
        if (information?.roles && information.roles.length > 0) setRoles(information.roles)
    }, [information])

    // Get user line preferences
    useEffect(() => {
        if (preferencesApiEnabled) {
            getPreferredLinesFromApi()
        }
        // the preferences are loaded once, when the page opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const parseRoleName = (role: string) => (role === 'no roles found' ? <i>{role}</i> : role)

    const onRefreshInformation = async () => {
        setIsUpdating(true)
        await rehydrateInformation()
        setIsUpdating(false)
    }

    const onLogout = useCallback(() => {
        switchSpace(undefined)
        logout()
        navigateTo('/identity/login')
    }, [switchSpace, logout, navigateTo])

    const productionLinesSettings = () => {
        return (
            <div className="settings-production-lines">
                <div className="settings-production-lines__title-wrapper">
                    <p className="settings-production-lines__title">
                    {t('general:settings:productionLines')}
                    </p>
                    {isSaving && (
                        <span className="settings-production-lines__status" role="status" aria-label={t('general:settings:saving')}>
                            <StreamingDots />
                        </span>
                    )}
                    {isSaved && (
                        <span className="settings-production-lines__status settings-production-lines__status--saved" role="status" aria-label={t('general:settings:saved')}>
                            <i className="a-icon boschicon-bosch-ic-checkmark" aria-hidden="true" />
                        </span>
                    )}
                </div>
                <DraggableLineSelection
                    availableLines={availableLines}
                    rows={rows}
                    setRows={setRows}
                    labelPrefix={t('general:settings:lineLabel')}
                    placeholder={t('general:settings:linePlaceholder')}
                    onChange={handleChange}
                />
                {saveError && (
                    <Notification
                        type="error"
                        defaultOpen
                        className="settings-production-lines__error"
                    >
                        {t('general:settings:saveError')}
                    </Notification>
                )}
            </div>
        )
    }

    return (
        <div className="settings-wrapper">
            <div className="user-information">
                <img className="login-photo" src={photo} alt="user" />
                <div className="user-name">{name}</div>
            </div>

            <p className="system-label">{t('general:settings:sessionSystemSettings')}</p>

            <div className="system-settings">
                <div className="settings-column">
                <div className="user-roles option">
                    <p>{t('general:settings:access')}</p>
                    <div className="user-roles-list">
                        {roles?.map((role, index) => (
                            <div key={index}>
                                {parseRoleName(role)}
                                {index < roles.length - 1 && ', '}
                            </div>
                        ))}
                        <div className="user-action-buttons">
                            <Button
                                className={`button-integrated-blue ${isUpdating ? 'loading' : ''}`}
                                mode="integrated"
                                label={t('general:buttons:refresh')}
                                icon={{ iconName: 'refresh' }}
                                disabled={isUpdating}
                                onClick={onRefreshInformation}
                            />
                            {!isMobile && (
                                <Button
                                    label={t('general:buttons:logout')}
                                    className="button-integrated-blue logout-button"
                                    mode="integrated"
                                    icon={{ iconName: 'logout' }}
                                    onClick={onLogout}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="settings-interface">
                    <p>{t('general:settings:theme')}</p>
                    <RadioButton
                        id="select-light"
                        label={t('general:settings:lightMode')}
                        checked={!darkTheme}
                        onChange={() => {
                            if (darkTheme) toggleTheme()
                        }}
                    />
                    <RadioButton
                        id="select-dark"
                        label={t('general:settings:darkMode')}
                        checked={darkTheme}
                        onChange={() => {
                            if (!darkTheme) toggleTheme()
                        }}
                    />
                </div>
                <div className="settings-language">
                    <p>{t('general:settings:language')}</p>
                    <LanguageSwitch />
                </div>
                </div>
                {productionLinesSettings()}
                {isMobile && (
                    <div className="settings-logout">
                        <Button
                            label={t('general:buttons:logout')}
                            className="button-integrated-blue logout-button"
                            mode="integrated"
                            icon={{ iconName: 'logout' }}
                            onClick={onLogout}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default Settings
